<template>
    <div ref="rootRef">
        <FormControlLabel :label="label" :name="name" :variant="variant" :required="required" />

        <PinInputRoot v-model="segments" :name="name" :placeholder="placeholder" :disabled="disabled" otp class="flex gap-2" @complete="onComplete">
            <PinInputInput
                v-for="index in length"
                :key="index"
                :id="index === 1 ? `${name}-input` : undefined"
                :index="index - 1"
                inputmode="numeric"
                :autofocus="autofocus && index === 1"
                :class="inputClasses"
            />
        </PinInputRoot>

        <div v-if="notice || hasError" class="mt-2">
            <FormControlInformation :error="error" :notice="notice" :name="name" />
        </div>
    </div>
</template>

<script setup lang="ts">
import FormControlInformation from '@/components/forms/FormControlInformation.vue';
import FormControlLabel from '@/components/forms/FormControlLabel.vue';
import { PinInputInput, PinInputRoot } from 'reka-ui';
import { computed, useTemplateRef } from 'vue';

type PinInputProps = {
    name: string;
    variant?: 'default' | 'customer';
    label?: string;
    placeholder?: string;
    length?: number;
    notice?: string;
    error?: string | string[] | Record<string, string>;
    required?: boolean;
    disabled?: boolean;
    autofocus?: boolean;
};

const props = withDefaults(defineProps<PinInputProps>(), {
    variant: 'default',
    label: undefined,
    placeholder: undefined,
    length: 6,
    notice: undefined,
    error: undefined,
    required: false,
    disabled: false,
    autofocus: false,
});

const emits = defineEmits<{ complete: [value: string] }>();

const modelValue = defineModel<string | undefined>();

// Exposed focus() lets callers refocus after a dialog step change, since reka-ui only autofocuses on initial open.
// Queried from a wrapping ref because a template ref on PinInputInput resolves to its component instance, not the <input>.
const rootRef = useTemplateRef<HTMLDivElement>('rootRef');

function focus(): void {
    rootRef.value?.querySelector<HTMLInputElement>('input')?.focus();
}

defineExpose({ focus });

const hasError = computed(() => {
    if (typeof props.error === 'object') {
        return Object.keys(props.error ?? {}).length > 0;
    }

    if (Array.isArray(props.error)) {
        return props.error.length > 0;
    }

    return !!props.error;
});

const segments = computed<string[]>({
    get: () => modelValue.value?.split('') ?? [],
    set: value => {
        modelValue.value = value.join('');
    },
});

function onComplete(value: string[]): void {
    emits('complete', value.join(''));
}

const inputClasses = computed(() => [
    'size-12 rounded-md bg-background text-center text-lg outline-1 -outline-offset-1 transition focus:outline-2 focus:-outline-offset-2 focus:outline-black disabled:cursor-not-allowed disabled:opacity-50',
    hasError.value ? 'outline-danger' : 'outline-gray-500',
]);
</script>
